/**
 * ASCII Art Generator - Vignette 晕影效果
 *
 * 使用径向渐变实现画面边缘渐暗效果
 */

import type { VignetteConfig } from './types'

// ============================================
// 纯函数计算
// ============================================

/**
 * 计算晕影强度因子
 * @param x 当前像素x坐标
 * @param y 当前像素y坐标
 * @param centerX 中心点x
 * @param centerY 中心点y
 * @param config 晕影配置
 * @returns 强度因子 (0-1，越大越暗)
 */
export function calculateVignetteFactor(
  x: number,
  y: number,
  centerX: number,
  centerY: number,
  config: VignetteConfig
): number {
  const { radius, softness } = config

  // 计算距离中心的距离比例
  const dx = x - centerX
  const dy = y - centerY
  const distance = Math.sqrt(dx * dx + dy * dy)
  const maxDistance = Math.max(centerX, centerY) * radius

  // 距离比例
  const ratio = distance / maxDistance

  // 使用软边缘过渡
  const innerRadius = 1 - softness
  if (ratio < innerRadius) {
    return 0 // 内部区域不受晕影影响
  }

  // 外部区域渐变
  const t = (ratio - innerRadius) / softness
  return Math.min(1, t)
}

// ============================================
// Canvas 应用函数
// ============================================

/**
 * 应用晕影效果到 Canvas
 * @param canvas 目标画布
 * @param config 晕影配置
 */
export function applyVignette(
  canvas: HTMLCanvasElement,
  config: VignetteConfig
): void {
  const ctx = canvas.getContext('2d')!
  const { width, height } = canvas
  const { strength, radius, softness } = config

  // 计算中心点和最大半径
  const centerX = width / 2
  const centerY = height / 2
  const maxRadius = Math.max(width, height) * radius

  // 创建径向渐变
  // 内半径：明亮区域
  // 外半径：暗化区域
  const innerRadius = maxRadius * (1 - softness)

  const gradient = ctx.createRadialGradient(
    centerX, centerY, innerRadius,
    centerX, centerY, maxRadius
  )

  // 强度映射到透明度 - 提高系数使效果更明显
  // strength = 0.5 时，边缘约 75% 暗度
  const darkAlpha = strength * 1.5

  // 渐变色值 - 更强的边缘暗化
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)')         // 中心透明
  gradient.addColorStop(0.4, `rgba(0, 0, 0, ${darkAlpha * 0.2})`) // 中间过渡
  gradient.addColorStop(0.7, `rgba(0, 0, 0, ${darkAlpha * 0.5})`) // 外围
  gradient.addColorStop(1, `rgba(0, 0, 0, ${darkAlpha})`)    // 边缘最暗

  // 使用 multiply 混合模式叠加
  ctx.globalCompositeOperation = 'multiply'
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, width, height)

  // 恢复默认混合模式
  ctx.globalCompositeOperation = 'source-over'
}

// ============================================
// 配置工厂函数
// ============================================

/**
 * 从用户设置创建晕影配置
 */
export function createVignetteConfigFromSettings(
  strength: number,
  radius: number,
  softness: number
): VignetteConfig {
  return {
    strength: Math.max(0, Math.min(1, strength)),
    radius: Math.max(0.3, Math.min(1, radius)),
    softness: Math.max(0.1, Math.min(0.5, softness))
  }
}

// ============================================
// 默认配置
// ============================================

/** 默认晕影配置 - 参数已调整使效果更明显 */
export const DEFAULT_VIGNETTE_CONFIG: VignetteConfig = {
  strength: 0.6,
  radius: 0.65,
  softness: 0.35
}